// src/components/EventDetailPanel.jsx
import { X, Clock, Calendar, User, Mail, CheckCircle, XCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import styles from './EventDetailPanel.module.css';

const TYPE_LABEL = {
  availability: 'Busy Time',
  booking: 'Booking',
  proposal: 'Proposal',
};

const STATUS_CLASS = {
  confirmed:  styles.statusConfirmed,
  cancelled:  styles.statusCancelled,
  pending:    styles.statusPending,
  accepted:   styles.statusAccepted,
  declined:   styles.statusDeclined,
};

function formatTimeRange(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  const mins = Math.round((e - s) / 60000);
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  const dur = hrs > 0 ? (rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`) : `${mins}m`;
  return `${format(s, 'h:mm a')} – ${format(e, 'h:mm a')} (${dur})`;
}

function AvailabilityContent({ event }) {
  const start = new Date(event.start);
  const end = new Date(event.end);
  const providerLabel = event.provider === 'google' ? 'Google Calendar' : event.provider === 'microsoft' ? 'Microsoft 365' : event.provider || 'External calendar';

  return (
    <div className={styles.content}>
      <div className={styles.field}>
        <Calendar size={16} className={styles.fieldIcon} aria-hidden="true" />
        <span>{format(start, 'EEEE, MMMM d, yyyy')}</span>
      </div>
      <div className={styles.field}>
        <Clock size={16} className={styles.fieldIcon} aria-hidden="true" />
        <span>{formatTimeRange(event.start, event.end)}</span>
      </div>
      <div className={styles.divider} />
      <div className={styles.meta}>
        <span className={styles.metaLabel}>Source</span>
        <span className={styles.metaValue}>{providerLabel}</span>
      </div>
      <div className={styles.meta}>
        <span className={styles.metaLabel}>Status</span>
        <span className={styles.metaValue}>Busy</span>
      </div>
    </div>
  );
}

function BookingContent({ event }) {
  const start = new Date(event.start);
  const statusKey = event.status || 'confirmed';
  const statusLabel = statusKey.charAt(0).toUpperCase() + statusKey.slice(1);
  const pillClass = STATUS_CLASS[statusKey] ?? styles.statusConfirmed;

  return (
    <div className={styles.content}>
      <div className={styles.bookingHeader}>
        <h3 className={styles.entityName}>{event.customerName || 'Unknown customer'}</h3>
        <span className={`${styles.statusPill} ${pillClass}`}>{statusLabel}</span>
      </div>

      {event.customerEmail && (
        <div className={styles.field}>
          <Mail size={16} className={styles.fieldIcon} aria-hidden="true" />
          <span>{event.customerEmail}</span>
        </div>
      )}

      <div className={styles.field}>
        <Calendar size={16} className={styles.fieldIcon} aria-hidden="true" />
        <span>{format(start, 'EEEE, MMMM d, yyyy')}</span>
      </div>

      <div className={styles.field}>
        <Clock size={16} className={styles.fieldIcon} aria-hidden="true" />
        <span>{formatTimeRange(event.start, event.end)}</span>
      </div>

      {event.notes && (
        <>
          <div className={styles.divider} />
          <div className={styles.notes}>
            <span className={styles.notesLabel}>Notes</span>
            <p className={styles.notesText}>{event.notes}</p>
          </div>
        </>
      )}

      {event.vennLinkId && (
        <>
          <div className={styles.divider} />
          <a
            href={`/dashboard`}
            className={styles.recordLink}
            aria-label="View booking record"
          >
            <ExternalLink size={14} aria-hidden="true" />
            View booking record
          </a>
        </>
      )}
    </div>
  );
}

function ProposalContent({ event, onAccept, onDecline, onCounter, loading }) {
  const start = new Date(event.start);
  const statusKey = event.status || 'pending';
  const isPending = statusKey === 'pending';
  const statusLabel = statusKey.charAt(0).toUpperCase() + statusKey.slice(1);
  const pillClass = STATUS_CLASS[statusKey] ?? styles.statusPending;

  const displayName = event.proposerName || event.customerName || 'Unknown';

  return (
    <div className={styles.content}>
      <div className={styles.bookingHeader}>
        <div>
          <p className={styles.proposerLabel}>Proposed by</p>
          <h3 className={styles.entityName}>{displayName}</h3>
        </div>
        <span className={`${styles.statusPill} ${pillClass}`}>{statusLabel}</span>
      </div>

      <div className={styles.field}>
        <Calendar size={16} className={styles.fieldIcon} aria-hidden="true" />
        <span>{format(start, 'EEEE, MMMM d, yyyy')}</span>
      </div>

      <div className={styles.field}>
        <Clock size={16} className={styles.fieldIcon} aria-hidden="true" />
        <span>{formatTimeRange(event.start, event.end)}</span>
      </div>

      {event.message && (
        <>
          <div className={styles.divider} />
          <div className={styles.notes}>
            <span className={styles.notesLabel}>Message</span>
            <p className={styles.notesText}>{event.message}</p>
          </div>
        </>
      )}

      {isPending && (onAccept || onDecline || onCounter) && (
        <>
          <div className={styles.divider} />
          <div className={styles.proposalActions}>
            {onDecline && (
              <button
                className={styles.btnDecline}
                onClick={() => onDecline(event.id)}
                disabled={loading}
              >
                <XCircle size={16} aria-hidden="true" />
                Decline
              </button>
            )}
            {onCounter && (
              <button
                className={styles.btnCounter}
                onClick={() => onCounter(event.id)}
                disabled={loading}
              >
                <RefreshCw size={16} aria-hidden="true" />
                Counter
              </button>
            )}
            {onAccept && (
              <button
                className={styles.btnAccept}
                onClick={() => onAccept(event.id)}
                disabled={loading}
              >
                <CheckCircle size={16} aria-hidden="true" />
                {loading ? 'Accepting…' : 'Accept'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Default counter handler — shown when no counter flow is wired.
// Informs the user that a counter-proposal flow is coming.
function defaultOnCounter() {
  // eslint-disable-next-line no-alert
  alert('Counter-proposal flow coming soon. For now, please contact the organiser directly to propose a new time.');
}

export default function EventDetailPanel({ event, onClose, onAccept, onDecline, onCounter, loading = false }) {
  // Always provide a counter handler for proposals so the button is visible per AC.
  const resolvedOnCounter = onCounter ?? (event?.type === 'proposal' ? defaultOnCounter : undefined);
  if (!event) return null;

  const typeLabel = TYPE_LABEL[event.type] ?? 'Event';

  const renderContent = () => {
    switch (event.type) {
      case 'booking':
        return <BookingContent event={event} />;
      case 'proposal':
        return <ProposalContent event={event} onAccept={onAccept} onDecline={onDecline} onCounter={resolvedOnCounter} loading={loading} />;
      case 'availability':
      default:
        return <AvailabilityContent event={event} />;
    }
  };

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={`${typeLabel} details`}
      >
        <div className={styles.header}>
          <span className={styles.typeLabel}>{typeLabel}</span>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close panel">
            <X size={18} />
          </button>
        </div>
        <div className={styles.body}>
          {renderContent()}
        </div>
      </div>
    </>
  );
}
